'use client';

import { useState } from 'react';
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { FileEarmarkPlus, Lock, ThreeDots, Trash, Unlock } from 'react-bootstrap-icons';
import { useSetInstanceLocked } from '@/queries/framework/lock-framework-config';
import { useSnackbar } from './SnackbarProvider';
type Props = {
  planId: string;
  instanceIdentifier?: string;
  planName: string;
  isLocked: boolean;
  canCreate: boolean;
  isAdmin: boolean;
  onCreateClick: () => void;
  onDeleteClick: () => void;
};
export function PlanActionsMenu({
  planId,
  instanceIdentifier,
  planName,
  isLocked,
  canCreate,
  isAdmin,
  onCreateClick,
  onDeleteClick
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = !!anchorEl;
  const {
    setNotification
  } = useSnackbar();
  const [lockPlan, {
    loading
  }] = useSetInstanceLocked(planId);
  function handleOpen(e: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(e.currentTarget);
  }
  function handleClose() {
    setAnchorEl(null);
  }
  async function handleLockToggle(instanceIdentifier: string) {
    handleClose();
    try {
      await lockPlan({
        variables: {
          instanceId: instanceIdentifier,
          isLocked: !isLocked
        }
      });
      setNotification({
        message: isLocked ? `"${planName}" unlocked — editing enabled.` : `"${planName}" locked — editing disabled.`,
        severity: 'success'
      });
    } catch {
      setNotification({
        message: 'Failed to update plan lock status. Please try again.',
        severity: 'error'
      });
    }
  }
  function handleDeleteClick() {
    handleClose();
    onDeleteClick();
  }
  function handleCreateClick() {
    handleClose();
    onCreateClick();
  }
  return <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }} data-sentry-element="Box" data-sentry-component="PlanActionsMenu" data-sentry-source-file="PlanActionsMenu.tsx">
      <IconButton color="inherit" onClick={handleOpen} disabled={loading} data-sentry-element="IconButton" data-sentry-source-file="PlanActionsMenu.tsx">
        <ThreeDots size={22} data-sentry-element="ThreeDots" data-sentry-source-file="PlanActionsMenu.tsx" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} slotProps={{
      paper: {
        sx: {
          minWidth: 240,
          mt: 0.75
        }
      }
    }} data-sentry-element="Menu" data-sentry-source-file="PlanActionsMenu.tsx">
        {canCreate && <MenuItem onClick={handleCreateClick}>
            <ListItemIcon>
              <FileEarmarkPlus size={16} />
            </ListItemIcon>
            <ListItemText primary="Create new plan" />
          </MenuItem>}

        {canCreate && isAdmin && <Divider />}

        {isAdmin && instanceIdentifier && <MenuItem onClick={() => void handleLockToggle(instanceIdentifier)}>
            <ListItemIcon>{isLocked ? <Unlock size={16} /> : <Lock size={16} />}</ListItemIcon>
            <ListItemText primary={isLocked ? 'Unlock plan' : 'Lock plan'} secondary={isLocked ? 'Allow edits again' : 'Prevent edits until unlocked'} />
          </MenuItem>}

        {isAdmin && <Divider />}

        {isAdmin && <MenuItem onClick={handleDeleteClick} sx={{
        color: 'error.dark'
      }}>
            <ListItemIcon sx={{
          color: 'error.dark'
        }}>
              <Trash size={16} />
            </ListItemIcon>
            <ListItemText primary="Delete plan" slotProps={{
          secondary: {
            color: 'error.dark',
            sx: {
              opacity: 0.8
            }
          }
        }} />
          </MenuItem>}
      </Menu>
    </Box>;
}